/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { tokens } from "../../theme";
import {
  Box,
  Grid,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Select from "../../components/SearchableSelect";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import Header from "../../components/Header";
import DataTable from "../../components/DataTable";
import DataGridButton from "../../components/DataGridButton";
import { codeCoverageItemAdapter } from "./utils";
import { useGetCodeCoverageData } from "../../api/codeCoverage";
import { useGetAppGroups, useGetReleases } from "../../api/releases";
import styles from "./styles";
import { Add, Remove, Sync } from "@mui/icons-material";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";
import { APPS, MAIN_BRANCH } from "../../utils/constants";
import { codeCoverageUrl } from "../../api/codeCoverage";
import { downloadFileFromUrl } from "../../utils/helperFunctions";
import { useReportModal } from "../../components/ReportModal";
import TimeCell from "../../components/TimeCell";

const queryParamsForGetReleases = "?loadRepos=false&loadCommits=false";

const CodeCoverage = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const [appGroups, setAppGroups] = useState([]);
  const [selectedAppGroup, setSelectedAppGroup] = useState("");
  const [releases, setReleases] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState(MAIN_BRANCH);
  const [codeCoverageData, setCodeCoverageData] = useState([]);
  const [expandedRow, setExpandedRow] = useState("");
  const [sortModel, setSortModel] = useState([]);

  const { postData: getAppGroups, response: getAppGroupsResp } =
    useGetAppGroups();

  const {
    postData: getCodeCoverageData,
    response: getCodeCoverageDataResp,
    isLoading: isLoadingCodeCoverageData,
  } = useGetCodeCoverageData(selectedAppGroup, selectedRelease);

  const { postData: getReleases, response: getReleasesResp } =
    useGetReleases(selectedAppGroup);

  const { reportModal, setReportModalOpen } = useReportModal();

  useEffect(() => {
    getAppGroups();
  }, []);

  useEffect(() => {
    if (getAppGroupsResp?.response?.length) {
      setAppGroups(getAppGroupsResp.response);
      setSelectedAppGroup(
        getAppGroupsResp?.response?.includes(APPS.TESSELL)
          ? APPS.TESSELL
          : getAppGroupsResp.response[0]
      );
    }
  }, [getAppGroupsResp]);

  useEffect(() => {
    if (selectedAppGroup !== "") {
      getReleases(queryParamsForGetReleases);
    }
  }, [selectedAppGroup]);

  useEffect(() => {
    if (getReleasesResp?.response?.length) {
      setReleases(
        [MAIN_BRANCH].concat(
          getReleasesResp.response?.map(
            (releaseJson) => releaseJson?.release_label
          )
        )
      );
      setSelectedRelease(MAIN_BRANCH);
    }
  }, [getReleasesResp]);

  useEffect(() => {
    if (selectedAppGroup) {
      getCodeCoverageData();
    }
  }, [selectedAppGroup, selectedRelease]);

  useEffect(() => {
    if (getCodeCoverageDataResp?.response) {
      setCodeCoverageData(
        getCodeCoverageDataResp.response?.map(codeCoverageItemAdapter)
      );
    }
  }, [getCodeCoverageDataResp]);

  const closeHistory = () => {
    setExpandedRow("");
    setCodeCoverageData(
      codeCoverageData?.filter((item) => {
        return item?.parentId === undefined;
      })
    );
  };

  const openHistory = (row) => {
    setSortModel([]);
    setExpandedRow(row?.id);
    const data = codeCoverageData
      ?.filter((item) => {
        return item?.parentId === undefined;
      })
      ?.map((item) => {
        if (item?.id !== row?.id) {
          return item;
        }
        return [row].concat(
          row?.history
            ?.map((historyItem) => {
              historyItem = codeCoverageItemAdapter(historyItem);
              historyItem.parentId = item.id;
              return historyItem;
            })
            ?.filter((_, i) => i !== 0)
        );
      })
      ?.flat();
    setCodeCoverageData(data);
  };

  const getShowHistoryIcon = (row) => {
    if (row?.parentId) {
      return <></>;
    }
    return row?.id === expandedRow ? <Remove /> : <Add />;
  };

  const getCoverageCell = (data) => {
    return (
      <Tooltip
        title={
          <>
            <Typography>Covered: {data?.covered}</Typography>
            <Typography>Skipped: {data?.skipped}</Typography>
            <Typography>Total: {data?.total}</Typography>
          </>
        }
      >
        <Typography sx={styles.coverageCell(colors, !data?.percentage)}>
          {(data?.percentage || "0") + "%"}
        </Typography>
      </Tooltip>
    );
  };

  const handleShowReport = (id) => {
    setReportModalOpen(
      `${codeCoverageUrl}/${id}/app-groups/${selectedAppGroup}/release-labels/${selectedRelease}/report`
    );
  };

  const columns = [
    {
      field: "actions",
      type: "actions",
      width: 44,
      getActions: ({ row }) => [
        <GridActionsCellItem
          label="Show History"
          disabled={row?.history?.length === 1}
          icon={getShowHistoryIcon(row)}
          onClick={() => {
            row.id === expandedRow ? closeHistory(row) : openHistory(row);
          }}
        />,
      ],
    },
    {
      field: "repoName",
      flex: 2,
      cellClassName: "name-column--cell",
      renderHeader: () => "Repository",
      renderCell: ({ row: { repoName } }) => repoName || "",
    },
    {
      field: "language",
      renderHeader: () => "Language",
      flex: 1,
      renderCell: ({ row: { language } }) => language || "",
    },
    {
      field: "report",
      renderHeader: () => "Report",
      flex: 1,
      sortable: false,
      renderCell: ({ row: { id, reportPath } }) => (
        <Stack direction="row">
          {reportPath?.endsWith(".html") ? (
            <DataGridButton
              sx={styles.reportButton(colors)}
              onClick={() => handleShowReport(id)}
            >
              View
            </DataGridButton>
          ) : (
            <DataGridButton
              sx={styles.reportButton(colors)}
              onClick={() =>
                downloadFileFromUrl(
                  `${codeCoverageUrl}/${id}/app-groups/${selectedAppGroup}/release-labels/${selectedRelease}/report`
                )
              }
            >
              Download
            </DataGridButton>
          )}
        </Stack>
      ),
    },
    {
      field: "tag",
      renderHeader: () => "Git Tag",
      flex: 1,
      renderCell: ({ row: { tag } }) => tag || "",
    },
    {
      field: "branchCoveragePercentage",
      renderHeader: () => "Branch %",
      flex: 1,
      renderCell: ({ row: { branchCoverage } }) =>
        getCoverageCell(branchCoverage),
    },
    {
      field: "statementCoveragePercentage",
      renderHeader: () => "Statement %",
      flex: 1,
      renderCell: ({ row: { statementCoverage } }) =>
        getCoverageCell(statementCoverage),
    },
    {
      field: "dateCreated",
      renderHeader: () => "Date Committed",
      flex: 1.3,
      minWidth: 118,
      renderCell: ({ row: { dateCreated } }) => (
        <TimeCell value={dateCreated} />
      ),
    },
  ];

  return (
    <Box sx={styles.mainBox}>
      {reportModal}
      <Grid sx={styles.headerGrid} container columns={2}>
        <Grid item xs={1}>
          <Header
            title="Code Coverage"
            subtitle={`Manage releases for: ${selectedAppGroup}`}
          />
        </Grid>
        <Grid item xs={1}>
          <Stack sx={styles.controlsStack}>
            <Box>
              <Typography sx={styles.selectLabel(colors)}>App Group</Typography>
              <Select
                id="select-app-group"
                value={selectedAppGroup || " "}
                sx={styles.select(colors)}
                onChange={(e) => setSelectedAppGroup(e.target.value)}
              >
                {appGroups.map((appGroup, i) => (
                  <MenuItem value={appGroup} key={i}>
                    {appGroup}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography sx={styles.selectLabel(colors)}>Release</Typography>
              <Select
                id="select-release"
                value={selectedRelease}
                sx={styles.select(colors)}
                onChange={(e) => setSelectedRelease(e.target.value)}
              >
                {releases.map((release, i) => (
                  <MenuItem value={release} key={i}>
                    {release}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <DataGridButton
              sx={styles.reloadButton}
              onClick={() => {
                getCodeCoverageData();
              }}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
          </Stack>
        </Grid>
      </Grid>

      <DataTable
        height="100%"
        sx={styles.dataTable}
        onSortModelChange={(model) => {
          setSortModel(model);
          closeHistory();
        }}
        sortModel={sortModel}
        getRowId={(row) => row?.id}
        loading={isLoadingCodeCoverageData}
        rows={codeCoverageData}
        columns={columns}
      />
    </Box>
  );
};

export default CodeCoverage;
